import React, { useEffect, useState } from 'react';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useAdminTheme } from '../../context/AdminThemeContext';
import AdminLayout from '../../components/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { 
  Plus, 
  Trash2, 
  Edit2, 
  X, 
  GripVertical, 
  Facebook, 
  Instagram,
  Youtube,
  ExternalLink,
  Eye,
  EyeOff,
  Save,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

// Normalize backend base
const rawBackend = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const BACKEND_BASE = rawBackend.replace(/\/+$|\/api$/i, '');
const API = `${BACKEND_BASE}/api`;

const getWebSocketUrl = () => {
  const wsBase = BACKEND_BASE.replace(/^http/i, 'ws');
  return `${wsBase}/api/ws`;
};

const EMBED_TYPES = [
  { id: 'facebook', label: 'Facebook', icon: Facebook, color: 'text-blue-600' },
  { id: 'instagram', label: 'Instagram', icon: Instagram, color: 'text-pink-600' },
  { id: 'youtube', label: 'YouTube', icon: Youtube, color: 'text-red-600' },
  { id: 'tiktok', label: 'TikTok', icon: ExternalLink, color: 'text-gray-800' },
];

// Extract src URL from iframe tag or return as-is if already a URL
const extractIframeSrc = (input) => {
  if (!input) return '';
  const trimmed = input.trim();
  
  // If it's an iframe tag, extract the src
  if (trimmed.toLowerCase().startsWith('<iframe')) {
    const srcMatch = trimmed.match(/src=["']([^"']+)["']/i);
    if (srcMatch && srcMatch[1]) {
      return srcMatch[1];
    }
  }
  
  // Otherwise return as-is (already a URL)
  return trimmed;
};

const AdminEmbedPage = () => {
  const { token, hasRole } = useAdminAuth();
  const { isDarkMode, theme } = useAdminTheme();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Modal state
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    embed_url: '',
    embed_type: 'facebook',
    is_active: true
  });

  // Preview state
  const [previewUrl, setPreviewUrl] = useState('');

  useEffect(() => {
    fetchPosts();
  }, []);

  // WebSocket for realtime updates
  useEffect(() => {
    if (!token) return;

    let ws;
    let reconnectTimer;
    let reconnectDelayMs = 2000;

    const connect = () => {
      ws = new WebSocket(getWebSocketUrl());

      ws.onmessage = (event) => {
        try {
          const msg = JSON.parse(event.data);
          if (!msg || typeof msg !== 'object') return;

          if (msg.type === 'embed_post_created' && msg.post?.embed_id) {
            setPosts((prev) => {
              if (prev.some((p) => p.embed_id === msg.post.embed_id)) return prev;
              return [...prev, msg.post].sort((a, b) => a.order - b.order);
            });
          }

          if (msg.type === 'embed_post_updated' && msg.post?.embed_id) {
            setPosts((prev) => prev.map((p) => 
              p.embed_id === msg.post.embed_id ? msg.post : p
            ));
          }

          if (msg.type === 'embed_post_deleted' && msg.embed_id) {
            setPosts((prev) => prev.filter((p) => p.embed_id !== msg.embed_id));
          }

          if (msg.type === 'embed_posts_reordered' && msg.posts) {
            setPosts(msg.posts);
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onopen = () => {
        reconnectDelayMs = 2000;
      };

      ws.onclose = () => {
        if (reconnectTimer) clearTimeout(reconnectTimer);
        reconnectTimer = setTimeout(() => {
          reconnectDelayMs = Math.min(reconnectDelayMs * 2, 30000);
          connect();
        }, reconnectDelayMs);
      };

      ws.onerror = () => {
        try { ws.close(); } catch { /* ignore */ }
      };
    };

    connect();
    return () => {
      if (reconnectTimer) clearTimeout(reconnectTimer);
      try { ws?.close(); } catch { /* ignore */ }
    };
  }, [token]);

  const fetchPosts = async () => {
    try {
      const res = await fetch(`${API}/admin/embed-posts`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPosts(data);
      }
    } catch (error) {
      console.error('Failed to load embed posts:', error);
      toast.error('Failed to load embed posts');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingPost(null);
    setFormData({
      title: '',
      embed_url: '',
      embed_type: 'facebook',
      is_active: true
    });
    setPreviewUrl('');
    setShowModal(true);
  };

  const openEditModal = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title || '',
      embed_url: post.embed_url || '',
      embed_type: post.embed_type || 'facebook',
      is_active: post.is_active !== false
    });
    setPreviewUrl(post.embed_url || '');
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPost(null);
    setFormData({
      title: '',
      embed_url: '',
      embed_type: 'facebook',
      is_active: true
    });
    setPreviewUrl('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }
    if (!formData.embed_url.trim()) {
      toast.error('Embed URL is required');
      return;
    }

    setSaving(true);
    try {
      const url = editingPost 
        ? `${API}/admin/embed-posts/${editingPost.embed_id}`
        : `${API}/admin/embed-posts`;
      
      const res = await fetch(url, {
        method: editingPost ? 'PUT' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (!res.ok) {
        throw new Error('Failed to save embed post');
      }

      toast.success(editingPost ? 'Embed post updated' : 'Embed post created');
      closeModal();
      fetchPosts();
    } catch (error) {
      console.error('Error saving embed post:', error);
      toast.error(error.message || 'Failed to save embed post');
    } finally {
      setSaving(false);
    }
  };

  const deletePost = async (embedId) => {
    if (!window.confirm('Are you sure you want to delete this embed post?')) return;

    try {
      const res = await fetch(`${API}/admin/embed-posts/${embedId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        throw new Error('Failed to delete embed post');
      }

      toast.success('Embed post deleted');
      fetchPosts();
    } catch (error) {
      console.error('Error deleting embed post:', error);
      toast.error(error.message || 'Failed to delete embed post');
    }
  };

  const toggleActive = async (post) => {
    try {
      const res = await fetch(`${API}/admin/embed-posts/${post.embed_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !post.is_active })
      });

      if (!res.ok) {
        throw new Error('Failed to update embed post');
      }

      toast.success(post.is_active ? 'Post hidden' : 'Post visible');
      fetchPosts();
    } catch (error) {
      console.error('Error updating embed post:', error);
      toast.error(error.message || 'Failed to update embed post');
    }
  };

  const movePost = async (embedId, direction) => {
    const currentIndex = posts.findIndex(p => p.embed_id === embedId);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= posts.length) return;

    const newOrder = [...posts];
    const [removed] = newOrder.splice(currentIndex, 1);
    newOrder.splice(newIndex, 0, removed);

    const orderList = newOrder.map(p => p.embed_id);

    try {
      const res = await fetch(`${API}/admin/embed-posts/reorder`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ order: orderList })
      });

      if (!res.ok) {
        throw new Error('Failed to reorder posts');
      }

      fetchPosts();
    } catch (error) {
      console.error('Error reordering posts:', error);
      toast.error('Failed to reorder posts');
    }
  };

  const getEmbedTypeInfo = (type) => {
    return EMBED_TYPES.find(t => t.id === type) || EMBED_TYPES[0];
  };

  const handleUrlChange = (input) => {
    // Extract URL from iframe tag if user pastes full iframe code
    const url = extractIframeSrc(input);
    setFormData(prev => ({ ...prev, embed_url: url }));
    setPreviewUrl(url);
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className={`text-2xl font-bold ${theme.text}`}>Embed Posts</h1>
            <p className={theme.textMuted}>Manage Facebook, Instagram, and other social media embeds</p>
          </div>
          <Button onClick={openAddModal} className="bg-blue-600 hover:bg-blue-700">
            <Plus className="h-4 w-4 mr-2" />
            Add Embed
          </Button>
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : posts.length === 0 ? (
          <Card className={isDarkMode ? 'bg-slate-800 border-slate-700' : ''}>
            <CardContent className="py-12 text-center">
              <Facebook className={`h-12 w-12 mx-auto mb-4 ${theme.textMuted}`} />
              <p className={theme.textMuted}>No embed posts yet</p>
              <p className={`text-sm ${theme.textMuted} mt-1`}>Click "Add Embed" to create your first social media embed</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {posts.map((post, index) => {
              const typeInfo = getEmbedTypeInfo(post.embed_type);
              const TypeIcon = typeInfo.icon;
              
              return (
                <Card 
                  key={post.embed_id} 
                  className={`w-full overflow-hidden ${isDarkMode ? 'bg-slate-800 border-slate-700' : ''} ${!post.is_active ? 'opacity-60' : ''}`}
                >
                  <CardContent className="p-4">
                    <div className="flex flex-col md:flex-row items-start gap-4">
                      {/* Drag Handle / Order */}
                      <div className="flex flex-col items-center gap-1 pt-1 flex-shrink-0">
                        <button
                          onClick={() => movePost(post.embed_id, 'up')}
                          disabled={index === 0}
                          className={`p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed`}
                        >
                          <GripVertical className="h-4 w-4 rotate-180" />
                        </button>
                        <span className={`text-xs font-medium ${theme.textMuted}`}>#{index + 1}</span>
                        <button
                          onClick={() => movePost(post.embed_id, 'down')}
                          disabled={index === posts.length - 1}
                          className={`p-1 rounded hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed`}
                        >
                          <GripVertical className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Type Icon */}
                      <div className={`p-3 rounded-lg flex-shrink-0 ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                        <TypeIcon className={`h-6 w-6 ${typeInfo.color}`} />
                      </div>

                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-semibold ${theme.text} truncate`}>{post.title}</h3>
                          {!post.is_active && (
                            <span className="px-2 py-0.5 text-xs rounded bg-amber-100 text-amber-700">
                              Hidden
                            </span>
                          )}
                        </div>
                        <p className={`text-sm ${theme.textMuted} truncate`}>{post.embed_url}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`px-2 py-0.5 text-xs rounded ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'} ${theme.textMuted}`}>
                            {typeInfo.label}
                          </span>
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleActive(post)}
                          title={post.is_active ? 'Hide post' : 'Show post'}
                        >
                          {post.is_active ? (
                            <Eye className="h-4 w-4 text-green-600" />
                          ) : (
                            <EyeOff className="h-4 w-4 text-gray-400" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditModal(post)}
                          className="z-10"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deletePost(post.embed_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {/* Preview */}
                    {post.is_active && post.embed_url && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        <div className="aspect-video max-h-[300px] rounded-lg overflow-hidden bg-gray-100 pointer-events-auto">
                          <iframe
                            src={post.embed_url}
                            className="w-full h-full"
                            style={{ border: 'none', minHeight: '250px' }}
                            scrolling="no"
                            frameBorder="0"
                            allowFullScreen={true}
                            allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                          />
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Add/Edit Modal */}
        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
            <div className={`w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl shadow-xl ${isDarkMode ? 'bg-slate-800' : 'bg-white'}`}>
              <div className="sticky top-0 flex items-center justify-between p-4 border-b border-gray-200 bg-inherit">
                <h2 className={`text-lg font-semibold ${theme.text}`}>
                  {editingPost ? 'Edit Embed Post' : 'Add Embed Post'}
                </h2>
                <Button variant="ghost" size="sm" onClick={closeModal}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <form onSubmit={handleSubmit} className="p-4 space-y-4">
                {/* Title */}
                <div className="space-y-2">
                  <Label className={theme.text}>Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., Club Highlights Video"
                    className={isDarkMode ? 'bg-slate-700 border-slate-600' : ''}
                  />
                </div>

                {/* Embed Type */}
                <div className="space-y-2">
                  <Label className={theme.text}>Platform</Label>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                    {EMBED_TYPES.map((type) => {
                      const Icon = type.icon;
                      return (
                        <button
                          key={type.id}
                          type="button"
                          onClick={() => setFormData(prev => ({ ...prev, embed_type: type.id }))}
                          className={`p-3 rounded-lg border-2 transition-all ${
                            formData.embed_type === type.id
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                        >
                          <Icon className={`h-5 w-5 mx-auto ${type.color}`} />
                          <span className={`text-xs mt-1 block ${theme.text}`}>{type.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Embed URL / Iframe Code */}
                <div className="space-y-2">
                  <Label className={theme.text}>Embed URL or Iframe Code</Label>
                  <textarea
                    value={formData.embed_url}
                    onChange={(e) => handleUrlChange(e.target.value)}
                    placeholder={'Paste URL or full <iframe> code:\nhttps://www.facebook.com/plugins/video.php?...\nor\n<iframe src="https://www.facebook.com/plugins/video.php?..."></iframe>'}
                    rows={3}
                    className={`w-full px-3 py-2 text-sm rounded-md border resize-none ${isDarkMode ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'} focus:outline-none focus:ring-2 focus:ring-blue-500`}
                  />
                  <p className={`text-xs ${theme.textMuted}`}>
                    Paste the embed URL or full iframe code - the URL will be extracted automatically
                  </p>
                </div>

                {/* Active Toggle */}
                <div className="flex items-center justify-between">
                  <div>
                    <Label className={theme.text}>Active</Label>
                    <p className={`text-xs ${theme.textMuted}`}>Show this embed on the landing page</p>
                  </div>
                  <Switch
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData(prev => ({ ...prev, is_active: checked }))}
                  />
                </div>

                {/* Preview */}
                {previewUrl && (
                  <div className="space-y-2">
                    <Label className={theme.text}>Preview</Label>
                    <div className={`rounded-lg overflow-hidden ${isDarkMode ? 'bg-slate-700' : 'bg-gray-100'}`}>
                      <iframe
                        src={previewUrl}
                        className="w-full"
                        style={{ border: 'none', minHeight: '400px' }}
                        scrolling="no"
                        frameBorder="0"
                        allowFullScreen={true}
                        allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                      />
                    </div>
                  </div>
                )}

                {/* Actions */}
                <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                  <Button type="button" variant="outline" onClick={closeModal}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700">
                    {saving ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        {editingPost ? 'Update' : 'Create'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminEmbedPage;
